import React from "react";
import Styled from 'styled-components'
import Fonts from "baseStyles/enums/fonts";
import { Colors } from "baseStyles/enums/colors";
import { TextComponentsType, TextPropsType, TextStylesTypes } from "./types";
import TextStyles from "./styles";
import { useSuiTheme } from "baseStyles/ThemeProvider";

const TextSpan = Styled.span<TextPropsType>`
    font-family: ${Fonts.Family.GRAPHIE};
    font-style: ${Fonts.Style.NORMAL};
    width: ${props => props.width ? `${props.width}px` : 'max-content'};
    max-width: ${props => props.maxWidth ? `${props.maxWidth}px` : 'max-content'};
    font-size: ${props => `${props.fontSize}px`};
    font-weight: ${props => `${props.fontWeight}`};
    line-height: ${props => `${props.lineHeight}px`};
    letter-spacing: ${props => `${props.letterSpacing || 0}px`};
    color: ${props => props.color || props.theme?.colorPrimary};
    ${props => props.fullWidth && `width: 100%;`}
    ${props => props.transition && `
      transition : ${typeof props.transition === "string" ? props.transition : "all 0.5s"};
    `};
    ${props => (props.width || props.fullWidth || props?.maxWidth) && `
        display: block;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    `}
    ${props => props.cta && `
        text-decoration: underline;
        cursor: ${props.disabled ? 'default' : 'pointer'};
        color: ${props.color ? props.color : props.theme?.colorLink};
        ${props.disabled && `
          color: ${props.theme?.colorLinkDisabled};
        `}
        transition: color .5s;

        &:hover {
          color: ${props.theme?.colorLinkHover};
        }
    `}
    ${props => props.noUnderline && `
      text-decoration: none;
    `}
    ${props => props.disabled && `
      pointer-events: none;
      cursor: default;
    `}
    ${props => props.center && `
      text-align: center;
    `}
    ${props => props.emphasis && `
      font-weight: ${(props.fontWeight === Fonts.Weight.W600) ? Fonts.Weight.W700 : Fonts.Weight.W600};
    `}

    opacity: ${props => props.opacity || 1};
    ${props => props.whiteSpace && `
      white-space: ${props.whiteSpace};
    `}
    ${props => props.overflowWrap && `
      overflow-wrap: ${props.overflowWrap};
    `}

    ${props => props.lines && `
      display: -webkit-box; 
      -webkit-box-orient: vertical; 
      -webkit-line-clamp: ${props.lines}; 
      white-space: break-spaces;
      word-wrap: break-word;
    `}

    ${props => props.inlineMaxWidth && `
        max-width: ${props.inlineMaxWidth};
        display: inline-block;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        vertical-align: bottom;
    `}

    ${props => props.notSelectable && `
        user-select: none;
    `}

    ${props => props.textAlign && `
        text-align: ${props.textAlign};
    `}

     ${props => props.textDecoration && `
       text-decoration: ${props.textDecoration};
    `}
    ${props => props.transition && `
        transition: ${props.transition};
    `}
    ${props => props?.flex && `
        flex: ${props.flex}; 
    `}
    :hover {
      ${props => props.hoverColor && `
      color: ${props.hoverColor};
    `};
`;

const Text: React.FC<TextPropsType> & TextComponentsType = ({ children, type, dataTestId, onClick, ...props }) => {
  const styles = TextStyles();
  const { theme } = useSuiTheme();

  return <TextSpan onClick={onClick} data-test-id={dataTestId} className={`sui-text`} {...(styles[type] || styles.NORMAL)} theme={theme} {...props}>{children}</TextSpan>;
};

Text[TextStylesTypes.HS1] = (props) => <Text type={TextStylesTypes.HS1} {...props}/>;
Text[TextStylesTypes.HS2] = (props) => <Text type={TextStylesTypes.HS2} {...props}/>;
Text[TextStylesTypes.H1] = (props) => <Text type={TextStylesTypes.H1} {...props}/>;
Text[TextStylesTypes.H2] = (props) => <Text type={TextStylesTypes.H2} {...props} />;
Text[TextStylesTypes.H3] = (props) => <Text type={TextStylesTypes.H3} {...props} />;
Text[TextStylesTypes.H4] = (props) => <Text type={TextStylesTypes.H4} {...props} />;
Text[TextStylesTypes.NORMAL] = (props) => <Text type={TextStylesTypes.NORMAL} {...props} />;
Text[TextStylesTypes.SMALL] = (props) => <Text type={TextStylesTypes.SMALL} {...props} />;
Text[TextStylesTypes.CAPTION] = (props) => <Text type={TextStylesTypes.CAPTION} {...props} />;
Text[TextStylesTypes.FOOTER] = (props) => <Text type={TextStylesTypes.FOOTER} {...props} />;

export default Text;